import { CONTROL_CHARS_RE } from './regex'

/**
 * Formats the provided date string to a human-readable format.
 *
 * @param date - The date string to format.
 * @returns The formatted date string.
 */
export function enforceLen({ str, len, ellipsis = false }: { str: string; len: number; ellipsis?: boolean }): string {
  str = str || ''
  if (str.length > len) {
    return str.slice(0, len) + (ellipsis ? '...' : '')
  }
  return str
}

/**
 * Sanitizes the provided string by removing control characters and trimming it.
 *
 * @param str - The string to sanitize.
 * @returns The sanitized string.
 */
export function sanitizeText(str: string): string {
  if (typeof str === 'string') {
    return str.replace(CONTROL_CHARS_RE, '').trim()
  }
  return ''
}

/**
 * Capitalizes the first letter of a string.
 *
 * @param str  - The string to capitalize.
 * @returns  - The capitalized string.
 */
export function capitalize(str: string): string {
  if (typeof str !== 'string') return ''
  if (str.length === 0) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * Generates initials from the provided first name and last name.
 *
 * @param fname - The first name of the person. Optional.
 * @param lname - The last name of the person. Optional.
 * @returns A string containing the initials of the first and last name.
 */
export function initials(fname?: string, lname?: string): string {
  return `${fname?.charAt(0) || ''}${lname?.charAt(0) || ''}`
}

/**
 * Get the first two letters from a given string.
 *
 * @param displayName a display name string or undefined value
 * @returns The first two letters if any, otherwise the first one only, otherwise, an
 * empty string.
 */
export function getFirstTwoLetters(displayName?: string): string {
  if (!displayName || displayName.length === 0) {
    return ''
  }

  if (displayName.length < 2) {
    return displayName[0]
  }

  return displayName[0] + displayName[1]
}

/**
 * Formats the provided date string to a human-readable format.
 *
 * @param date - The date string to format.
 * @returns The formatted date string.
 */
export function formatSize(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

export const formatCurrency = (price: number, currency?: string) => {
  const formatted = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(price)

  return formatted
}

export const formatCurrencyCents = (priceCents: number, currency?: string, countryCode?: string) => {
  const formatted = new Intl.NumberFormat(countryCode || 'fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
    currencySign: 'accounting',
    currencyDisplay: 'symbol',
  }).format(priceCents / 100)

  return formatted
}
